import type { HttpErrorResponse } from '@angular/common/http';

import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import {
  IApplication,
  IApplicationListItem,
} from 'src/app/shared/interfaces/account/account.interface';
import {
  IUserPermission,
  IUserPermissionREST,
} from 'src/app/shared/interfaces/permissions.interface';
import {
  AccessLevelsGraphQL,
  AccessLevelsRESTOptions,
  IApplicationREST,
  IUser,
  IUserAccountDTO,
  IUserAccountREST,
  IUserApplication,
  IUserBaseREST,
  IUserCreateREST,
  IUserGroupNested,
  IUserUpdateREST,
} from 'src/app/shared/interfaces/user/user.interface';
import { AlertService } from 'src/app/shared/services/alert.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { UserStateService } from 'src/app/shared/services/users/user-state.service';
import { UsersService } from 'src/app/shared/services/users/users.service';
import {
  DEFAULT_MODAL_OPTIONS,
  EMAIL_REGEX,
} from 'src/app/shared/variables/variables';

import { AddAccountToUserModalComponent } from '../add-account-to-user-modal/add-account-to-user-modal.component';
import { AddCompanyToUserModalComponent } from '../add-company-to-user-modal/add-company-to-user-modal.component';
import { AddGroupToUserModalComponent } from '../add-group-to-user-modal/add-group-to-user-modal.component';
import {
  IUserSearchModalResult,
  UserSearchModalComponent,
} from '../user-search-modal/user-search-modal.component';

@Component({
  selector: 'user-form',
  templateUrl: './create-user-form.component.html',
  styleUrls: ['./create-user-form.component.scss'],
})
export class CreateUserFormComponent implements OnInit, OnDestroy {
  @Input() editableUser?: IUser;
  @Input() profileMode = false;

  @Output() submitEvent = new EventEmitter();

  formUser: UntypedFormGroup;
  currentUser?: IUser;

  userVendors?: IApplicationListItem[];
  userGroups?: IUserGroupNested[];
  userAccounts?: IUserAccountDTO[];

  checkedAccounts?: IUserAccountDTO[];
  checkedGroups?: IUserGroupNested[];

  permissions?: IUserPermission[];

  isLoading = false;
  isUserTypeSelected = false;
  isEditMode = false;
  isAdditionalDataValid = false;

  isAdditionalDataRequired: IAdditionalDataRequirements;

  AccessLevels: AccessLevelsGraphQL;

  private additionalRequiredDataByAccessLevel: Record<
    string,
    IAdditionalDataRequirements
  >;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private modal: NgbModal,
    private usersService: UsersService,
    private alertService: AlertService,
    private modalService: ModalService,
    private userStateService: UserStateService,
    private fb: UntypedFormBuilder,
  ) {
    this.currentUser = this.userStateService.user;
    this.permissions = this.currentUser?.permissions;
  }

  ngOnInit() {
    console.log(this.permissions);
    this.initAdditionalRequiredDataByAccessLevel();

    this.isEditMode = !!this.editableUser;

    const { editableUser } = this;
    if (editableUser) {
      this.setEditableUser(editableUser);
    }

    this.setUserForm(editableUser);

    this.subscriptions.add(
      this.formUser.get('accessLevel')?.valueChanges.subscribe((value) => {
        const accessLevel: AccessLevelsGraphQL = value || '';
        this.isAdditionalDataRequired =
          this.additionalRequiredDataByAccessLevel[accessLevel];
      }),
    );

    this.subscriptions.add(
      this.formUser.valueChanges.subscribe(() => {
        this.isUserTypeSelected = !!this.formUser.value.accessLevel;
        this.updateAdditionalDataValidity();
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  isOptionAvilable(optionValue: AccessLevelsGraphQL) {
    return this.usersService.isAvilableByAccessLevel(
      optionValue,
      this.currentUser?.accessLevel,
    );
  }

  onSubmit() {
    this.isLoading = true;

    const userModal: IUser = {
      ...this.editableUser,
      ...this.formUser.value,
      role: this.editableUser?.role ?? 'SCADMIN',
    };

    this.formatUserData(userModal);

    const request$ = this.editableUser
      ? this.usersService.updateUser(this.mapUserToUserUpdateREST(userModal))
      : this.usersService.createUser(this.mapUserToUserSaveREST(userModal));
    const succesMessage = this.editableUser
      ? 'User updated successfully!'
      : 'User successfully created!';

    this.subscriptions.add(
      request$.subscribe({
        next: () => {
          this.submitEvent.emit();
          this.alertService.success(succesMessage);
          this.isLoading = false;
        },
        error: (error: unknown) => {
          if (error) {
            this.alertService.error(error as HttpErrorResponse | string);
          }
          this.isLoading = false;
        },
      }),
    );
  }

  private mapUserToUserSaveREST(user: IUser): IUserCreateREST {
    return {
      userName: user.userName,
      ...this.mapUserToUserBaseREST(user),
    };
  }

  private mapUserToUserUpdateREST(user: IUser): IUserUpdateREST {
    return {
      id: user.id,
      ...this.mapUserToUserBaseREST(user),
    };
  }

  private mapUserToUserBaseREST(user: IUser): IUserBaseREST {
    const model: IUserBaseREST = {
      firstName: user.firstName,
      lastName: user.lastName,
      caseNumber: user.caseNumber,
      role: user.role,
      active: user.active,
      passwordResetAllowed: user.passwordResetAllowed,
      accessLevel: user.accessLevel
        ? AccessLevelsRESTOptions[user.accessLevel]
        : 'SecureConnect',
      applications: user.applications?.map((x) => {
        return {
          applicationId: x.code,
          name: x.description,
        } as IApplicationREST;
      }),
      accounts: user.accounts?.map((x) => {
        return {
          account: x.accountNumber,
          name: x.name,
        } as IUserAccountREST;
      }),
      groups: user.groups,
      permissions: user.permissions?.map((x) => {
        return {
          id: x.permissionId,
          name: x.name,
        } as IUserPermissionREST;
      }),
      pin: user.pin,
      phone: user.phone,
      ext: user.ext,
      cellPhone: user.cellPhone,
    };

    return model;
  }

  /** Add vendor   */
  async addApplication() {
    const modal = this.modal.open(AddCompanyToUserModalComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      scrollable: false,
    });

    const modalInstance =
      modal.componentInstance as AddCompanyToUserModalComponent;

    modalInstance.applications = this.userVendors;
    modalInstance.loadDataOnModalOpen =
      this.currentUser?.accessLevel != 'SECURE_CONNECT';

    const applications = await modal.result;

    this.userVendors = applications;
    this.updateAdditionalDataValidity();
  }

  async removeApplication(app: IApplication) {
    const modalText = {
      bodyTitle: 'Are you sure you want to delete this vendor?',
      bodyText: 'This will remove vendor from this user.',
    };

    await this.modalService.open('confirm', modalText).result;

    this.userVendors = this.userVendors?.filter((item) => {
      return item.code !== app.code && item.description !== app.description;
    });

    this.userAccounts = [];
    this.checkedAccounts = [];
    this.userGroups = [];
    this.checkedGroups = [];

    this.updateAdditionalDataValidity();

    this.isEditMode = false;
  }

  async addAccountModal() {
    const modal = this.modal.open(AddAccountToUserModalComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      scrollable: false,
    });

    const modalInstance =
      modal.componentInstance as AddAccountToUserModalComponent;

    modalInstance.userAccounts = this.checkedAccounts;
    modalInstance.userApplications = this.userVendors;

    const result = await modal.result;

    this.userAccounts = result;
    this.checkedAccounts = result;

    this.updateAdditionalDataValidity();
  }

  async addGroupModal() {
    const modal = this.modal.open(AddGroupToUserModalComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      scrollable: false,
    });

    const modalInstance =
      modal.componentInstance as AddGroupToUserModalComponent;
    modalInstance.userAccounts = this.checkedAccounts;
    modalInstance.userGroups = this.checkedGroups;

    const result = await modal.result;

    this.userGroups = result;
    this.checkedGroups = result;
    this.updateAdditionalDataValidity();
  }

  async copyUserPermissions() {
    await this.copyFromUser({ isPermissions: true });
  }

  async copyUserGroups() {
    await this.copyFromUser({ isGroups: true });
  }

  setAccountsToUser(accounts: IUserAccountDTO[]) {
    this.userAccounts = accounts;
  }

  setCheckedAccounts(accounts: IUserAccountDTO[]) {
    if (this.userAccounts) {
      if (accounts.length < this.userAccounts.length) {
        this.userAccounts = accounts;
      }
    }

    this.checkedAccounts = accounts;

    this.updateAdditionalDataValidity();
  }

  setGroupsToUser(groups: IUserGroupNested[]) {
    this.userGroups = groups;
    this.updateAdditionalDataValidity();
  }

  setCheckedGroups(groups: IUserGroupNested[]) {
    this.isEditMode = false;
    this.checkedGroups = groups;

    if (this.userGroups) {
      if (groups.length < this.userGroups.length) {
        this.userGroups = groups;
      }
    }

    this.updateAdditionalDataValidity();
  }

  private async copyFromUser(params: ICopyFromUserParams) {
    const modal = this.modal.open(UserSearchModalComponent, {
      ...DEFAULT_MODAL_OPTIONS,
      scrollable: false,
    });

    const modalInstance = modal.componentInstance as UserSearchModalComponent;

    if (this.currentUser?.accessLevel) {
      modalInstance.currentUserAccessLevel = this.currentUser.accessLevel;
    }
    modalInstance.groups = this.currentUser?.groups;
    modalInstance.selectedAccountNumbers = this.userAccounts?.map((x) => {
      return x.accountNumber;
    });
    modalInstance.permissions = this.permissions;
    modalInstance.isPermissionMode = !!params.isPermissions;

    const result = await modal.result;

    const user = result as IUserSearchModalResult;

    if (params.isPermissions) {
      if (user.permissions) {
        const filtredPermissions = [...user.permissions];

        const formUserPermissions = this.formUser.get('permissions');
        formUserPermissions?.setValue(filtredPermissions);
      }
    }

    if (params.isGroups) {
      this.userGroups = user.groups;

      this.updateAdditionalDataValidity();
    }
  }

  private formatUserData(user: IUser) {
    user.applications = this.userVendors?.map((app) => {
      return { ...app } as IUserApplication;
    });
    user.accounts = this.userAccounts ?? [];
    user.groups = this.checkedGroups ?? [];
  }

  private setEditableUser(editableUser: IUser) {
    if (editableUser.applications) {
      this.userVendors = this.mapApplicationsToApplicationListItems(
        editableUser.applications,
      );
    }
    this.userAccounts = editableUser.accounts;
    this.checkedAccounts = this.userAccounts;
    this.userGroups = editableUser.groups;
    this.checkedGroups = this.userGroups;

    if (editableUser.accessLevel) {
      this.isUserTypeSelected = true;
    }

    this.updateAdditionalDataValidity();
  }

  private mapApplicationsToApplicationListItems(
    applications: IApplicationREST[] | IUserApplication[],
  ): IApplicationListItem[] {
    return applications
      .map((x) => {
        if (!x) {
          return null;
        }
        return {
          code:
            (x as IUserApplication).code ||
            (x as IApplicationREST).applicationId,
          description:
            (x as IUserApplication).description || (x as IApplicationREST).name,
        } as IApplicationListItem;
      })
      .filter((x) => {
        return !!x;
      });
  }

  private setUserForm(editableUser?: IUser) {
    this.formUser = this.fb.group({
      cellPhone: [editableUser?.cellPhone ?? undefined],
      phone: [
        editableUser?.phone ?? undefined,
        [Validators.required, Validators.maxLength(10)],
      ],
      userName: [
        {
          value: editableUser?.userName ?? undefined,
          disabled: editableUser ? true : false,
        },
        [Validators.required, Validators.pattern(EMAIL_REGEX)],
      ],
      ext: [editableUser?.ext ?? undefined, [Validators.maxLength(6)]],
      firstName: [
        editableUser?.firstName ?? undefined,
        [Validators.required, Validators.maxLength(32)],
      ],
      lastName: [
        editableUser?.lastName ?? undefined,
        [Validators.required, Validators.maxLength(32)],
      ],
      caseNumber: [
        {
          value: editableUser?.caseNumber ?? undefined,
          disabled: editableUser ? true : false,
        },
        [Validators.required, Validators.maxLength(25)],
      ],
      pin: [
        editableUser?.pin ?? undefined,
        [Validators.required, Validators.minLength(4), Validators.maxLength(4)],
      ],
      accessLevel: [editableUser?.accessLevel ?? '', [Validators.required]],
      active: [editableUser ? editableUser.active : true],
      passwordResetAllowed: [
        editableUser ? editableUser.passwordResetAllowed : true,
      ],
      permissions: [editableUser?.permissions ?? '', [Validators.required]],
    });

    if (editableUser) {
      setTimeout(() => {
        this.formUser.markAllAsTouched();

        const accessLevel = this.formUser.value.accessLevel || '';
        this.isAdditionalDataRequired =
          this.additionalRequiredDataByAccessLevel[accessLevel];
        this.updateAdditionalDataValidity();
      });
    }

    if (this.profileMode) {
      for (const field of Object.keys(this.formUser.controls)) {
        this.formUser.get(field)?.disable();
        this.formUser.get(field)?.clearValidators();
        this.formUser.get(field)?.updateValueAndValidity();
      }
    }
  }

  private initAdditionalRequiredDataByAccessLevel() {
    const data: Record<AccessLevelsGraphQL, IAdditionalDataRequirements> =
      {} as Record<AccessLevelsGraphQL, IAdditionalDataRequirements>;
    data.SECURE_CONNECT = {
      vendors: false,
      accounts: false,
      groups: false,
    };
    data.VENDOR = {
      vendors: true,
      accounts: false,
      groups: false,
    };
    data.ACCOUNT = {
      vendors: true,
      accounts: true,
      groups: false,
    };
    data.BILLING_GROUP = {
      vendors: true,
      accounts: true,
      groups: true,
    };
    data[''] = { ...data.BILLING_GROUP };

    this.additionalRequiredDataByAccessLevel = data;
    this.isAdditionalDataRequired = data[''];
  }

  private updateAdditionalDataValidity() {
    let additionalRequiredVendors = true;
    if (this.userVendors) {
      if (this.userVendors.length === 0) {
        additionalRequiredVendors = false;
      }
    } else {
      additionalRequiredVendors = false;
    }

    let additionalRequiredAccounts = true;
    if (this.checkedAccounts) {
      if (this.checkedAccounts.length === 0) {
        additionalRequiredAccounts = false;
      }
    } else {
      additionalRequiredAccounts = false;
    }

    let additionalRequiredGroups = true;
    if (this.userGroups) {
      if (this.userGroups.length === 0) {
        additionalRequiredGroups = false;
      }
    } else {
      additionalRequiredGroups = false;
    }

    this.isAdditionalDataValid =
      additionalRequiredVendors &&
      additionalRequiredAccounts &&
      additionalRequiredGroups;
  }
}

export type ICopyFromUserParams = {
  isGroups?: boolean;
  isPermissions?: boolean;
};

type IAdditionalDataRequirements = {
  accounts: boolean;
  groups: boolean;
  vendors: boolean;
};
